<template>
  <div class="number">
    <textInput
      class="input-field"
      type="number"
      :disabled="disabled"
      :error="fieldIsBlankError"
      :label="label"
      :placeholder="placeholder"
      :prepend-inner-icon="icon"
      :prefix="prefix"
      ref="input"
      :rules="validationRules"
      :value="safeValue"
      :warning="warning"
      :width="width"
      @change="changeHandler"
      @input="inputHandler"
      @wheel="(e) => e.target.blur()"
      validate-on-blur
    >
      <template
        v-if="!hideTooltip && !hideLabel && (!!tooltip || !!helpContent)"
        #append-label
      >
        <tooltip v-if="tooltip" :text="tooltip" />
        <helpIcon v-if="helpContent" :content="helpContent" />
      </template>
    </textInput>
    <template v-if="!hideTooltip && hideLabel && (!!tooltip || !!helpContent)">
      <tooltip v-if="tooltip" :text="tooltip" />
      <helpIcon v-if="helpContent" :content="helpContent" />
    </template>
  </div>
</template>

<script>
/**
 * @fileoverview Config input field for numbers
 *
 * @author Cameron Bulock <cameron.bulock@quavo.com>
 */

import fieldMap from "./field-map.json";
import getValidationRules from "@/util/getValidationRules";
import helpIcon from "@/components/help-icon";
import textInput from "@/qux-common/qux22/components/molecules/input";
import tooltip from "@/components/tooltip";

export default {
  name: "NumberField",
  components: { helpIcon, textInput, tooltip },
  data() {
    return {
      fieldIsBlankError: false,
    };
  },
  props: {
    category: {
      type: String,
    },
    disabled: {
      type: Boolean,
    },
    helpContent: {
      type: String,
    },
    hideLabel: {
      type: Boolean,
    },
    hideTooltip: {
      type: Boolean,
    },
    icon: {
      type: String,
    },
    id: {
      type: String,
    },
    label: {
      type: String,
    },
    parentField: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    prefix: {
      type: String,
    },
    rules: {
      type: Array,
    },
    tooltip: {
      type: String,
    },
    value: {
      type: Number,
      default: null,
    },
    warning: {
      type: String,
    },
    width: {
      type: String,
    },
  },
  methods: {
    setConfigValue(value) {
      this.$emit("setConfigValue", value !== undefined ? value.trim() : '');
    },
    getFieldValue(value) {
      return (
        fieldMap[this.category] &&
        fieldMap[this.category][this.id] &&
        fieldMap[this.category][this.id][value]
      );
    },
    changeHandler(value) {
      // default to '' if value is blank
      const valueToSet = value || '';

      this.setConfigValue(valueToSet);
      this.fieldIsBlankError = false;
      // need to force this to update, doesn't seem to consistantly update on it's own
      this.$refs.input.$refs.input.lazyValue = valueToSet;
    },

    inputHandler() {
      this.fieldIsBlankError = !this.$refs.input.$el.querySelector("input").value;
    },
    validate() {
      return this.$refs.input.validate();
    },
  },
  computed: {
    safeValue() {
      if (typeof this.value !== 'number' || isNaN(this.value)) {
        return '';
      }
      return this.value;
    },
    validationRules() {
      return getValidationRules({
        rules: this.rules,
        inputErrorMessage: this.getFieldValue("inputErrorMessage"),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.number {
  display: flex;
  gap: $qvo-spacing-8;
  .input-field {
    width: 100%;
  }
  &::v-deep {
    .v-input {
      input {
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        -moz-appearance: textfield;
      }
    }
  }
}
</style>